import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  Alert,
  Stack,
  Tooltip,
  IconButton,
  alpha,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  Add as AddIcon,
  GroupAdd as GroupAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LockReset as LockResetIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from "@mui/icons-material";
import BulkUserDialog from "../../components/users/BulkUserDialog";
import { useAuthStore } from "../../stores/authStore";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  userAPI,
  User,
  CreateUserData,
  UpdateUserData,
  UserSortField,
  UserSortOrder,
  RolePack,
} from "../../services/userAPI";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { useRBAC } from "../../hooks/useRBAC";
import { getAssignableFeaturesForRole } from "../../constants/featurePermissions";
import { roleAPI } from "../../services/roleAPI";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import HolderAvatar from "../../components/layout/HolderAvatar";
import TableActionButton from "../../components/layout/TableActionButton";
import { tokens } from "../../theme";
import { formatRoleLabel } from "../../constants/roleLabels";

const MPIN_ROLES = ["OPERATOR", "MERCHANT", "GROCERY_STORE"] as const;
/** Roles that must pick a sales point. Branch ADMIN must not. */
const SALES_POINT_REQUIRED_ROLES = new Set([
  "SALES_POINT_ADMIN",
  "OPERATOR",
  "MERCHANT",
  "GROCERY_STORE",
]);

const userSchema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email format").required("Email is required"),
  // Never hard-required here — create path checks password in onSubmit (edit hides the field).
  password: yup
    .string()
    .transform((value, original) =>
      original === "" || original == null ? undefined : value
    )
    .notRequired()
    .min(8, "Password must be at least 8 characters"),
  role: yup.string().required("Role is required"),
  branchId: yup.string().required("Branch ID is required"),
  salesPointId: yup
    .string()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue == null ? null : value
    )
    .when("role", {
      is: (role: string) => SALES_POINT_REQUIRED_ROLES.has(role),
      then: (schema) =>
        schema.required("Sales point is required for this role"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  phone: yup.string().optional(),
  mpin: yup
    .string()
    .transform((value, original) =>
      original === "" || original == null ? undefined : value
    )
    .notRequired()
    .matches(/^\d{6}$/, "MPIN must be exactly 6 digits"),
  isActive: yup.boolean().required("Status is required"),
});

type UserFormData = yup.InferType<typeof userSchema>;
type RoleFilter = "all" | string;
type StatusFilter = "all" | "active" | "inactive";
type UiSortOrder = "asc" | "desc";

/** Shrink column to content; table scrolls horizontally when needed */
const fitContentCell = {
  width: "1%",
  whiteSpace: "nowrap" as const,
};

const branchCellSx = {
  maxWidth: 88,
  width: 88,
  px: 1,
  whiteSpace: "nowrap" as const,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<UserSortField>("name");
  const [sortOrder, setSortOrder] = useState<UiSortOrder>("asc");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const [openDialog, setOpenDialog] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    userId: string | null;
  }>({ open: false, userId: null });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  const { canCreate, canUpdate, canDelete, canAssignFeaturePermissions } = useRBAC();
  const [featurePermissionsForm, setFeaturePermissionsForm] = useState<
    Record<string, boolean>
  >({});
  const [selectedPackId, setSelectedPackId] = useState("");
  const { user: currentUser } = useAuthStore();
  const roleUpper = String(currentUser?.role || "").toUpperCase();
  const isSuperAdmin =
    roleUpper === "SUPER_ADMIN" || roleUpper === "SUPERADMIN";
  const isAdmin = roleUpper === "ADMIN";
  const canResetCredentials = isSuperAdmin || isAdmin;
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [mpinDialog, setMpinDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });
  const [manualMpin, setManualMpin] = useState("");
  const [mpinDialogError, setMpinDialogError] = useState<string | null>(null);

  const { data: catalogRoles = [] } = useQuery({
    queryKey: ["roles", "active"],
    queryFn: () => roleAPI.list({ isActive: "true" }),
  });

  const assignableRoles = catalogRoles.filter((r) => {
    if (!r.isActive) return false;
    if (r.key === "SUPER_ADMIN") return isSuperAdmin;
    if (isSuperAdmin) return true;
    if (isAdmin) {
      return ["SALES_POINT_ADMIN", "OPERATOR", "MERCHANT", "GROCERY_STORE"].includes(r.key);
    }
    return ["OPERATOR", "MERCHANT", "GROCERY_STORE"].includes(r.key);
  });

  /** Super Admin can assign Branch Admin; Branch Admin manages SP-scoped roles only. */
  const formatRole = (role: string, catalogName?: string) =>
    formatRoleLabel(role, catalogName);

  const filterRoles = catalogRoles.filter((r) => {
    if (r.key === "SUPER_ADMIN") return false;
    if (isSuperAdmin) return true;
    if (isAdmin) {
      return ["SALES_POINT_ADMIN", "OPERATOR", "MERCHANT", "GROCERY_STORE"].includes(r.key);
    }
    return false;
  });

  useEffect(() => {
    if (isSuperAdmin && roleFilter === "SUPER_ADMIN") {
      setRoleFilter("all");
    }
    if (isAdmin && !isSuperAdmin && (roleFilter === "SUPER_ADMIN" || roleFilter === "ADMIN")) {
      setRoleFilter("all");
    }
  }, [isAdmin, isSuperAdmin, roleFilter]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "users",
      currentUser?.id,
      currentUser?.role,
      page,
      rowsPerPage,
      roleFilter,
      statusFilter,
      debouncedSearch,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      userAPI.getUsers({
        pageSize: rowsPerPage,
        pageNo: page + 1,
        sortBy,
        sortOrder: sortOrder.toUpperCase() as UserSortOrder,
        ...(roleFilter !== "all" ? { role: roleFilter } : {}),
        ...(statusFilter === "active" ? { isActive: true } : {}),
        ...(statusFilter === "inactive" ? { isActive: false } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }),
    enabled: !!currentUser?.id,
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["sales_points", "all"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  /** Branch Admin should only see levels below (never self / peer ADMIN). */
  const allowedListRoles = isSuperAdmin
    ? null
    : isAdmin
      ? new Set(["SALES_POINT_ADMIN", "OPERATOR", "MERCHANT", "GROCERY_STORE"])
      : new Set(["OPERATOR", "MERCHANT", "GROCERY_STORE"]);

  const users = (data?.users ?? []).filter((u: User) => {
    if (currentUser?.id && u.id === currentUser.id) return false;
    if (!allowedListRoles) return true;
    return allowedListRoles.has(String(u.role || "").toUpperCase());
  });

  const { mutate: createUser } = useMutation({
    mutationFn: (payload: CreateUserData) => userAPI.createUser(payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setAlert({
        type: "success",
        message: MPIN_ROLES.includes(created?.role as (typeof MPIN_ROLES)[number])
          ? "User created successfully. MPIN is set for login."
          : "User created successfully!",
      });
      setModalError(null);
      handleCloseDialog();
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || "Failed to create user";
      const validationErrors = err.response?.data?.validation;
      if (validationErrors) {
        const validationMessages = Object.entries(validationErrors)
          .map(([field, message]) => `${field}: ${message}`)
          .join("\n");
        setModalError(validationMessages);
      } else {
        setModalError(errorMessage);
      }
    },
  });

  const { mutate: updateUser, isPending: isUpdatingUser } = useMutation({
    mutationFn: ({ id, data: payload }: { id: string; data: UpdateUserData }) =>
      userAPI.updateUser(id, payload),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      const oneTime = updated?.temporaryPassword
        ? ` New password (shown once): ${updated.temporaryPassword}`
        : updated?.mpin
          ? " MPIN updated."
          : "";
      setAlert({
        type: "success",
        message: `User updated successfully!${oneTime}`,
      });
      handleCloseDialog();
      setMpinDialog({ open: false, user: null });
      setManualMpin("");
      setMpinDialogError(null);
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || "Failed to update user";
      const validationErrors = err.response?.data?.validation;
      if (validationErrors) {
        const validationMessages = Object.entries(validationErrors)
          .map(([field, message]) => `${field}: ${message}`)
          .join("\n");
        setModalError(validationMessages);
      } else {
        setModalError(errorMessage);
      }
    },
  });

  const { mutate: deleteUser } = useMutation({
    mutationFn: (id: string) => userAPI.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setAlert({ type: "success", message: "User deleted successfully!" });
      setDeleteDialog({ open: false, userId: null });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete user",
      });
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: yupResolver(userSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      mpin: "",
      role: "OPERATOR",
      branchId: "",
      salesPointId: "",
      phone: "",
      isActive: true,
    },
  });

  const selectedRole = watch("role");
  const selectedSalesPointId = watch("salesPointId");
  const selectedSalesPoint = salesPoints.find(
    (sp: { id: string; workstationType?: string }) => sp.id === selectedSalesPointId
  );
  const isMpinRole = MPIN_ROLES.includes(selectedRole as (typeof MPIN_ROLES)[number]);
  const salesPointRequired = SALES_POINT_REQUIRED_ROLES.has(selectedRole);
  const assignableFeatures = getAssignableFeaturesForRole(selectedRole || "OPERATOR");

  useEffect(() => {
    if (!salesPointRequired && selectedSalesPointId) {
      setValue("salesPointId", "");
    }
  }, [salesPointRequired, selectedSalesPointId, setValue]);

  const { data: rolePacksData } = useQuery({
    queryKey: ["role-packs", selectedSalesPoint?.workstationType || "none", openDialog],
    queryFn: () =>
      userAPI.getRolePacks(selectedSalesPoint?.workstationType),
    enabled: openDialog && !editingUser && !viewOnly,
  });

  function applyRolePack(pack: RolePack) {
    setSelectedPackId(pack.id);
    setValue("role", pack.role);
    setFeaturePermissionsForm({ ...(pack.featurePermissions || {}) });
  }

  useEffect(() => {
    if (!openDialog || editingUser || viewOnly) return;
    const suggestedId = rolePacksData?.suggestedPackId;
    if (!suggestedId || selectedPackId) return;
    const pack = rolePacksData?.packs.find((p) => p.id === suggestedId);
    if (pack) applyRolePack(pack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolePacksData?.suggestedPackId, openDialog, editingUser, viewOnly]);

  const openUserDialog = async (user?: User, readOnly = false) => {
    setSelectedPackId("");
    setViewOnly(readOnly);
    if (user) {
      let detail = user;
      if (readOnly) {
        try {
          detail = await userAPI.getUserById(user.id);
        } catch {
          detail = user;
        }
      }
      setEditingUser(detail);
      reset({
        name: detail.name,
        email: detail.email,
        password: "",
        mpin: "",
        role: detail.role,
        branchId: detail.branchId || "",
        salesPointId: detail.salesPointId || "",
        phone: detail.phone || "",
        isActive: detail.isActive,
      });
      setFeaturePermissionsForm(detail.featurePermissions ?? {});
    } else {
      setEditingUser(null);
      reset({
        name: "",
        email: "",
        password: "",
        mpin: "",
        role: "OPERATOR",
        branchId: isAdmin && currentUser?.branchId ? currentUser.branchId : "",
        salesPointId: "",
        phone: "",
        isActive: true,
      });
      setFeaturePermissionsForm({});
    }
    setModalError(null);
    setOpenDialog(true);
  };

  /** Open edit dialog when navigated from user profile (Edit button). */
  useEffect(() => {
    const editUserId = (location.state as { editUserId?: string } | null)?.editUserId;
    if (!editUserId || !users.length) return;
    const match = users.find((u: User) => u.id === editUserId);
    if (match) {
      void openUserDialog(match);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, users]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setViewOnly(false);
    setModalError(null);
    setFeaturePermissionsForm({});
    setSelectedPackId("");
    reset();
  };

  const onSubmit = (formData: UserFormData) => {
    if (viewOnly) return;
    setModalError(null);
    const needsSalesPoint = SALES_POINT_REQUIRED_ROLES.has(formData.role);
    if (editingUser) {
      const updateData: UpdateUserData = {
        name: formData.name,
        email: formData.email,
        branchId: formData.branchId,
        salesPointId: needsSalesPoint ? formData.salesPointId || null : null,
        phone: formData.phone,
        isActive: formData.isActive,
        role: formData.role,
      };
      if (canResetCredentials && formData.password) {
        updateData.password = formData.password;
      }
      if (canResetCredentials && isMpinRole && formData.mpin) {
        updateData.mpin = formData.mpin;
      }
      if (canAssignFeaturePermissions()) {
        updateData.featurePermissions = featurePermissionsForm;
      }
      updateUser({ id: editingUser.id, data: updateData });
    } else {
      const needsPassword = !MPIN_ROLES.includes(
        formData.role as (typeof MPIN_ROLES)[number]
      );
      if (needsPassword && !formData.password) {
        setModalError("Password is required");
        return;
      }
      const payload: CreateUserData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        branchId:
          isAdmin && currentUser?.branchId ? currentUser.branchId : formData.branchId,
        phone: formData.phone,
      };
      if (needsSalesPoint && formData.salesPointId) {
        payload.salesPointId = formData.salesPointId;
      }
      if (needsPassword) {
        payload.password = formData.password!;
      } else if (formData.mpin) {
        payload.mpin = formData.mpin;
      }
      if (canAssignFeaturePermissions()) {
        payload.featurePermissions = featurePermissionsForm;
      }
      if (selectedPackId) {
        payload.rolePackId = selectedPackId;
      }
      createUser(payload);
    }
  };

  const roleChip = (role: string) => {
    const styles: Record<string, { bg: string; color: string }> = {
      SUPER_ADMIN: { bg: alpha(tokens.colors.error.main, 0.12), color: tokens.colors.error.main },
      ADMIN: { bg: alpha(tokens.colors.dashOrange, 0.15), color: tokens.colors.dashOrange },
      SALES_POINT_ADMIN: { bg: alpha(tokens.colors.dashBlue, 0.1), color: tokens.colors.dashBlue },
      OPERATOR: { bg: alpha(tokens.colors.dashBlue, 0.12), color: tokens.colors.dashBlue },
      MERCHANT: { bg: alpha(tokens.colors.success.main, 0.12), color: tokens.colors.success.main },
      GROCERY_STORE: { bg: alpha(tokens.colors.success.main, 0.18), color: tokens.colors.success.main },
    };
    const s = styles[role] || { bg: tokens.colors.neutral[100], color: tokens.colors.neutral[600] };
    return (
      <Chip
        label={formatRole(role)}
        size="small"
        sx={{ fontWeight: 600, fontSize: "0.75rem", bgcolor: s.bg, color: s.color, border: "none" }}
      />
    );
  };

  const handleSort = (field: UserSortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(0);
  };

  const sortableHeader = (
    label: string,
    field: UserSortField,
    options?: { align?: "right"; compact?: boolean; sx?: SxProps<Theme> }
  ) => (
    <TableCell
      align={options?.align}
      sortDirection={sortBy === field ? sortOrder : false}
      sx={options?.sx ?? (options?.compact ? fitContentCell : undefined)}
    >
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortOrder : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const statusChip = (active: boolean) => (
    <Chip
      label={active ? "Active" : "Inactive"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        bgcolor: active ? tokens.colors.success.light : tokens.colors.error.light,
        color: active ? tokens.colors.success.main : tokens.colors.error.main,
        border: "none",
      }}
    />
  );

  const filterDropdowns = (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
        <InputLabel>Role</InputLabel>
        <Select
          value={roleFilter}
          label="Role"
          onChange={(e) => {
            setRoleFilter(e.target.value as RoleFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Roles</MenuItem>
          {filterRoles.map((r) => (
            <MenuItem key={r.key} value={r.key}>
              {formatRoleLabel(r.key, r.name)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  const filteredSalesPoints = salesPoints.filter(
    (d: { branchId?: string }) =>
      isSuperAdmin || !isAdmin || !currentUser?.branchId || d.branchId === currentUser.branchId
  );

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle={
          isAdmin && !isSuperAdmin
            ? "Operators and merchants in your branch"
            : isSuperAdmin
              ? "Admins, operators, and merchants across all branches"
              : "Manage system users and their roles"
        }
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Users" },
        ]}
        action={
          <>
            <Tooltip title="Export PDF">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Excel">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <ExcelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isSuperAdmin && (
              <Button
                variant="outlined"
                startIcon={<GroupAddIcon />}
                onClick={() => setBulkDialogOpen(true)}
              >
                Bulk Add
              </Button>
            )}
            {canCreate("users") && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openUserDialog()}>
                Add User
              </Button>
            )}
          </>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading users: {error.message || "Please try again later"}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search name, email or phone…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={filterDropdowns}
        footer={
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={data?.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Loading users…</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}
            >
              <TableHead>
                <TableRow>
                  <TableCell width={72} sx={fitContentCell}>
                    S.No.
                  </TableCell>
                  {sortableHeader("User", "name")}
                  {sortableHeader("Email", "email", { compact: true })}
                  {sortableHeader("Phone", "phone", { compact: true })}
                  {sortableHeader("Role", "role", { compact: true })}
                  <TableCell sx={fitContentCell}>MPIN</TableCell>
                  {sortableHeader("Branch", "branch", { sx: branchCellSx })}
                  {sortableHeader("SalesPoint", "salesPoint", { compact: true })}
                  {sortableHeader("Status", "status", { compact: true })}
                  <TableCell align="right" sx={fitContentCell}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user: User & { branch?: { name: string }; salesPoint?: { name: string } }, index: number) => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2" color="text.secondary">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <HolderAvatar name={user.name} size={38} />
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                              "&:hover": { color: "primary.main", textDecoration: "underline" },
                            }}
                            onClick={() => navigate(`/users/${user.id}`)}
                          >
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {formatRole(user.role)}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>{user.phone || "—"}</TableCell>
                    <TableCell sx={fitContentCell}>{roleChip(user.role)}</TableCell>
                    <TableCell sx={fitContentCell}>
                      {MPIN_ROLES.includes(
                        user.role as (typeof MPIN_ROLES)[number]
                      ) ? (
                        canResetCredentials ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<LockResetIcon sx={{ fontSize: 16 }} />}
                            onClick={() => {
                              setManualMpin("");
                              setMpinDialogError(null);
                              setMpinDialog({ open: true, user });
                            }}
                            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                          >
                            Set MPIN
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={branchCellSx}>
                      <Tooltip
                        title={(user as any).branch?.name || ""}
                        disableHoverListener={!(user as any).branch?.name}
                      >
                        <Typography variant="body2" noWrap>
                          {(user as any).branch?.name || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2">
                        {(user as any).salesPoint?.name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>{statusChip(user.isActive)}</TableCell>
                    <TableCell align="right" sx={fitContentCell}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <TableActionButton
                          title="View profile"
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          <ViewIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                        {canUpdate("users") && (
                          <TableActionButton
                            title="Edit user"
                            variant="primary"
                            onClick={() => openUserDialog(user)}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canDelete("users") && (
                          <TableActionButton
                            title="Delete user"
                            variant="danger"
                            onClick={() => setDeleteDialog({ open: true, userId: user.id })}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {viewOnly ? "View User" : editingUser ? "Edit User" : "Add User"}
          </DialogTitle>
          {modalError && (
            <Alert severity="error" sx={{ mx: 3, mb: 2 }}>
              {modalError.split("\n").map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </Alert>
          )}
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.25 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Name"
                      disabled={viewOnly}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email"
                      disabled={viewOnly}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              {!editingUser && !viewOnly && !isMpinRole && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Password"
                        type="password"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                      />
                    )}
                  />
                </Grid>
              )}
              {!editingUser && !viewOnly && isMpinRole && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="mpin"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ""}
                        fullWidth
                        label="MPIN (optional)"
                        inputProps={{
                          maxLength: 6,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        error={!!errors.mpin}
                        helperText={
                          errors.mpin?.message ||
                          "Leave blank to auto-generate a 6-digit MPIN"
                        }
                      />
                    )}
                  />
                </Grid>
              )}
              {editingUser &&
                !viewOnly &&
                canResetCredentials &&
                currentUser?.id !== editingUser.id &&
                !isMpinRole && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="New password (optional)"
                          type="password"
                          autoComplete="new-password"
                          error={!!errors.password}
                          helperText={
                            errors.password?.message ||
                            "Leave blank to keep current. Shown once after save."
                          }
                        />
                      )}
                    />
                  </Grid>
                )}
              {editingUser &&
                !viewOnly &&
                canResetCredentials &&
                currentUser?.id !== editingUser.id &&
                isMpinRole && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="mpin"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ""}
                          fullWidth
                          label="New MPIN (optional)"
                          inputProps={{
                            maxLength: 6,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          error={!!errors.mpin}
                          helperText={
                            errors.mpin?.message ||
                            "Enter a 6-digit MPIN to reset. Leave blank to keep current."
                          }
                        />
                      )}
                    />
                  </Grid>
                )}
              {!editingUser && !viewOnly && rolePacksData?.packs?.length ? (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Role pack (template)</InputLabel>
                    <Select
                      label="Role pack (template)"
                      value={selectedPackId}
                      onChange={(e) => {
                        const pack = rolePacksData.packs.find(
                          (p) => p.id === e.target.value
                        );
                        if (pack) applyRolePack(pack);
                      }}
                    >
                      <MenuItem value="">
                        <em>Choose a preset…</em>
                      </MenuItem>
                      {rolePacksData.packs.map((pack) => (
                        <MenuItem key={pack.id} value={pack.id}>
                          {pack.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {selectedPackId && (
                      <FormHelperText>
                        {
                          rolePacksData.packs.find((p) => p.id === selectedPackId)
                            ?.description
                        }
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              ) : null}
              {editingUser?.rolePackId && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Role pack: {editingUser.rolePackId.replace(/^pack_/, "").replace(/_/g, " ")}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.role} disabled={viewOnly}>
                      <InputLabel>Role</InputLabel>
                      <Select {...field} label="Role">
                        {assignableRoles.map((r) => (
                          <MenuItem key={r.key} value={r.key}>
                            {formatRoleLabel(r.key, r.name)}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.role && <FormHelperText>{errors.role.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="branchId"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      error={!!errors.branchId}
                      disabled={viewOnly || (isAdmin && !isSuperAdmin)}
                    >
                      <InputLabel>Branch</InputLabel>
                      <Select {...field} label="Branch">
                        <MenuItem value="">
                          <em>Select a branch</em>
                        </MenuItem>
                        {branches.map((branch: { id: string; name: string }) => (
                          <MenuItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.branchId && (
                        <FormHelperText>{errors.branchId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="salesPointId"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      error={!!errors.salesPointId}
                      disabled={viewOnly || !salesPointRequired}
                    >
                      <InputLabel>
                        Sales point{salesPointRequired ? "" : " (not needed)"}
                      </InputLabel>
                      <Select
                        {...field}
                        value={field.value || ""}
                        label={
                          salesPointRequired
                            ? "Sales point"
                            : "Sales point (not needed)"
                        }
                      >
                        <MenuItem value="">
                          <em>
                            {salesPointRequired
                              ? "Select a sales point"
                              : "Not required for this role"}
                          </em>
                        </MenuItem>
                        {filteredSalesPoints.map((salesPoint: { id: string; name: string }) => (
                          <MenuItem key={salesPoint.id} value={salesPoint.id}>
                            {salesPoint.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.salesPointId && (
                        <FormHelperText>{errors.salesPointId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Phone"
                      disabled={viewOnly}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                    />
                  )}
                />
              </Grid>
              {canAssignFeaturePermissions() &&
                !viewOnly &&
                assignableFeatures.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      Extra permissions
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Optional — shown by module (only for this user)
                    </Typography>
                    <Stack spacing={1.5}>
                      {Object.entries(
                        assignableFeatures.reduce(
                          (acc: Record<string, typeof assignableFeatures>, feat) => {
                            const g = feat.moduleLabel || feat.module || "Other";
                            if (!acc[g]) acc[g] = [];
                            acc[g].push(feat);
                            return acc;
                          },
                          {}
                        )
                      ).map(([moduleLabel, feats]) => (
                        <Box key={moduleLabel}>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color="text.secondary"
                            sx={{ textTransform: "uppercase", letterSpacing: 0.04 }}
                          >
                            {moduleLabel}
                          </Typography>
                          <Stack spacing={0.5} mt={0.5}>
                            {feats.map((feat) => (
                              <FormControlLabel
                                key={feat.key}
                                control={
                                  <Checkbox
                                    checked={Boolean(featurePermissionsForm[feat.key])}
                                    onChange={(e) =>
                                      setFeaturePermissionsForm((prev) => ({
                                        ...prev,
                                        [feat.key]: e.target.checked,
                                      }))
                                    }
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {feat.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {feat.description}
                                    </Typography>
                                  </Box>
                                }
                              />
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Grid>
                )}
              {viewOnly && assignableFeatures.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Extra permissions
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {assignableFeatures.map((feat) => (
                      <Chip
                        key={feat.key}
                        size="small"
                        label={feat.label}
                        color={
                          featurePermissionsForm[feat.key] ? "success" : "default"
                        }
                        variant={featurePermissionsForm[feat.key] ? "filled" : "outlined"}
                      />
                    ))}
                  </Stack>
                </Grid>
              )}
              {viewOnly &&
                editingUser &&
                MPIN_ROLES.includes(
                  editingUser.role as (typeof MPIN_ROLES)[number]
                ) && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      MPIN is not shown. Use Set MPIN to assign a new 6-digit code.
                    </Alert>
                  </Grid>
                )}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.isActive} disabled={viewOnly}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={field.value ? "true" : "false"}
                        label="Status"
                        onChange={(e) => field.onChange(e.target.value === "true")}
                      >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                      </Select>
                      {errors.isActive && (
                        <FormHelperText>{errors.isActive.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog} color="inherit">
              {viewOnly ? "Close" : "Cancel"}
            </Button>
            {!viewOnly && (
              <Button type="submit" variant="contained">
                {editingUser ? "Save Changes" : "Add User"}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      <BulkUserDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        branches={branches}
        salesPoints={filteredSalesPoints.map((d: { id: string; name: string; branchId?: string }) => ({
          id: d.id,
          name: d.name,
          branchId: d.branchId || "",
        }))}
        onSuccess={(message) => {
          setAlert({ type: "success", message });
        }}
        onError={(message) => {
          setAlert({ type: "error", message });
        }}
      />

      <Dialog
        open={mpinDialog.open}
        onClose={() => {
          if (isUpdatingUser) return;
          setMpinDialog({ open: false, user: null });
          setManualMpin("");
          setMpinDialogError(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Set MPIN</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a new 6-digit MPIN for{" "}
            <strong>{mpinDialog.user?.name || "this user"}</strong>. The current
            MPIN is never shown.
          </Typography>
          {mpinDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mpinDialogError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="New MPIN"
            value={manualMpin}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
              setManualMpin(digits);
              setMpinDialogError(null);
            }}
            inputProps={{
              maxLength: 6,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
            helperText="Exactly 6 digits"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMpinDialog({ open: false, user: null });
              setManualMpin("");
              setMpinDialogError(null);
            }}
            disabled={isUpdatingUser}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={isUpdatingUser}
            onClick={() => {
              if (!mpinDialog.user) return;
              if (!/^\d{6}$/.test(manualMpin)) {
                setMpinDialogError("MPIN must be exactly 6 digits");
                return;
              }
              updateUser({
                id: mpinDialog.user.id,
                data: { mpin: manualMpin },
              });
            }}
          >
            {isUpdatingUser ? "Saving…" : "Save MPIN"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: null })}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete user</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, userId: null })}>Cancel</Button>
          <Button
            onClick={() => deleteDialog.userId && deleteUser(deleteDialog.userId)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
